<template>
  <div class="mp">
    <h2 class="mp-page-title">How Others See You</h2>

    <!-- Profile header: avatar on the left (pencil overlaid), name + signs right. -->
    <!-- Header skeleton: the same footprint the real header will occupy, so the
         page does not jump when it lands. -->
    <div v-if="loading" class="mp-head mp-skeleton" aria-hidden="true">
      <div class="mp-sk mp-sk-avatar"></div>
      <div class="mp-name-wrap">
        <div class="mp-sk mp-sk-line" style="width: 140px; height: 22px;"></div>
        <div class="mp-sk mp-sk-line" style="width: 60px; height: 13px; margin-top: 8px;"></div>
      </div>
    </div>

    <div v-else class="mp-head">
      <div class="mp-avatar" @click="profile.avatar_url && (lightbox = true)"
           :class="{ clickable: profile.avatar_url }">
        <img v-if="profile.avatar_url" :src="profile.avatar_url" alt="Your avatar" />
        <span v-else class="mp-avatar-initial">{{ initial }}</span>
        <button class="mp-avatar-edit" title="Edit avatar" aria-label="Edit avatar"
                @click.stop="editAvatar"><i class="fas fa-pencil"></i></button>
      </div>
      <div class="mp-name-wrap">
        <div class="mp-name-row">
          <h2 class="mp-name">{{ profile.nick_name || 'Your Mirror' }}</h2>
          <button class="mp-name-edit" title="Change your name" aria-label="Change your name"
                  @click="openNamePicker"><i class="fas fa-pencil"></i></button>
        </div>
        <!-- Birth details — cake + pencil only (the exact date is kept private). -->
        <button class="mp-born" @click="openBirth" :title="bornLabel ? 'Edit birth details' : 'Add your birth details'">
          <i class="fas fa-cake-candles mp-born-icon"></i>
          <i class="fas fa-pencil mp-born-edit"></i>
        </button>
      </div>
    </div>

    <!-- Mirror tabs (2026-10-29, MIRROR-OUTLINE.md): Profile · Shared · Circle
         · Looks. Before the Community opens a newcomer has none of the share
         switches yet (gift bug #36, navigation §14). The Quest journey (API v1
         §2 mirror_sections): Profile and Looks come with the Mirror, the
         Circle when Venus draws, Shared with the Community. -->
    <div v-if="hasSection('profile') && (hasSection('circle') || hasSection('looks') || hasSection('shared'))" class="mp-tabs" data-testid="mp-tabs">
      <button :class="['mp-tab', { active: mirrorTab === 'profile' }]" data-testid="mp-tab-profile" @click="mirrorTab = 'profile'">Profile</button>
      <button v-if="hasSection('shared')" :class="['mp-tab', { active: mirrorTab === 'shared' }]" data-testid="mp-tab-shared" @click="mirrorTab = 'shared'">Shared</button>
      <button v-if="hasSection('circle')" :class="['mp-tab', { active: mirrorTab === 'seekers' }]" data-testid="mp-tab-circle" @click="mirrorTab = 'seekers'">Circle</button>
      <button v-if="hasSection('looks')" :class="['mp-tab', { active: mirrorTab === 'looks' }]" data-testid="mp-tab-looks" @click="mirrorTab = 'looks'">Looks</button>
    </div>

    <!-- Avatar lightbox -->
    <div v-if="lightbox && profile.avatar_url" class="mp-lightbox" @click="lightbox = false">
      <img :src="profile.avatar_url" alt="Your avatar" class="mp-lightbox-img" @click.stop />
    </div>

    <!-- Sign-image lightbox -->
    <div v-if="signLightbox" class="mp-lightbox" @click="signLightbox = null">
      <img :src="signLightbox" alt="Your sign" class="mp-lightbox-img" @click.stop />
    </div>

    <!-- Birth details: the one birth component (design 2026-10-26): the
         birthday, with the time and place folded (open when saved), each
         optional. Saving closes it: the Mirror already shows the signs. -->
    <BirthDetailsModal v-if="birthModalOpen" title="Your birth details"
      subtitle="Your birthday shows your Sun sign. A time and place add your Moon and Rising."
      :optional="['time', 'place']" submit-label="Save"
      :moment="birthMoment" :saving="savingBirth" :error="birthError" :server-errors="birthFieldErrors"
      @save="saveBirth" @close="birthModalOpen = false" />

    <!-- PROFILE tab: the signs (with the custom-image builder) and where they
         meet, then "What others see": the altar, the affirmation and the
         Letter from Mystic AIda, each with its switch (members only). -->
    <div class="mp-section" v-show="mirrorTab === 'profile'" data-testid="mp-panel-profile">
      <!-- Sun, Moon & Rising — the default glyph shows until the seeker paints a
           custom sign image (the pencil opens the Mystic AIda builder). A sign we
           can't compute simply isn't in signList, so nothing renders for it. -->
      <div v-if="loading" class="mp-signs-about mp-skeleton" aria-hidden="true">
        <div v-for="n in 3" :key="n" class="mp-sign-card mp-sk-card">
          <div class="mp-sk mp-sk-visual"></div>
          <div class="mp-sign-text">
            <div class="mp-sk mp-sk-line" style="width: 66px; height: 14px;"></div>
            <div class="mp-sk mp-sk-line" style="width: 34px; height: 10px; margin-top: 6px;"></div>
          </div>
        </div>
      </div>
      <div v-else-if="signList.length" class="mp-signs-about">
        <div v-for="s in signList" :key="s.which" class="mp-sign-card">
          <button v-if="s.img" class="mp-sign-visual is-img" :title="`${s.value} ${s.role}`" @click="signLightbox = s.img">
            <img :src="s.img" :alt="`${s.value} ${s.role}`" />
          </button>
          <span v-else class="mp-sign-visual is-chip"><span class="mp-glyph">{{ glyph(s.value) }}</span></span>
          <div class="mp-sign-text">
            <span class="mp-sign-name">{{ s.value }}</span>
            <span class="mp-sign-role">{{ s.role }}</span>
          </div>
          <button class="mp-sign-edit" :title="`Create a custom ${s.value} ${s.role} image`" @click="editSign(s.which, s.value)">
            <i class="fas fa-pencil"></i>
          </button>
        </div>
      </div>

      <!-- About — what the three signs say TOGETHER. The cards above already
           say what each one means alone; this is the intersection, which is the
           only thing worth putting underneath them. -->
      <div v-if="about.has_blurb || aboutWriting" class="mp-about">
        <p v-if="about.text" class="mp-about-text">{{ about.text }}</p>
        <p v-else class="mp-about-writing mp-about-text">
          <span class="mp-about-dots"><i></i><i></i><i></i></span>
          Reading where your signs meet…
        </p>
        <div v-if="about.has_blurb && memberMirror" class="mp-about-share">
          <span class="mp-about-share-label">Show on your Profile</span>
          <label class="mp-switch">
            <input type="checkbox" :checked="about.is_public" @change="toggleAbout($event)" />
            <span class="mp-switch-track"><span class="mp-switch-knob"></span></span>
          </label>
        </div>
      </div>

      <!-- What others see (members only): each public thing with its switch. -->
      <div v-if="memberMirror" class="mp-others" data-testid="mp-what-others-see">
      <div class="mp-section-label">What others see</div>
      <p v-if="memberMirror && !loading && !altar.has_image && !affirmation.has_affirmation" class="mp-empty">Share your altar or affirmation (from the Altar) and the toggles appear here.</p>
      <div class="mp-altar-row" v-if="altar.has_image">
        <div class="mp-altar-thumb">
          <img :src="altar.image_url" alt="Your altar" />
        </div>
        <div class="mp-altar-copy">
          <div class="mp-altar-title">Share your altar</div>
          <p class="mp-altar-sub">Show your altar on your Mirror.</p>
        </div>
        <label class="mp-switch">
          <input type="checkbox" :checked="altar.is_public" @change="toggleAltar($event)" />
          <span class="mp-switch-track"><span class="mp-switch-knob"></span></span>
        </label>
      </div>

      <div class="mp-altar-row" :class="{ 'mp-row-spaced': altar.has_image }" v-if="affirmation.has_affirmation">
        <div class="mp-altar-copy">
          <div class="mp-altar-title">Share your affirmation</div>
          <p class="mp-altar-sub">{{ affirmation.text || 'Show your affirmation on your Mirror.' }}</p>
        </div>
        <label class="mp-switch">
          <input type="checkbox" :checked="affirmation.is_public" @change="toggleAffirmation($event)" />
          <span class="mp-switch-track"><span class="mp-switch-knob"></span></span>
        </label>
      </div>

      <!-- A Letter from Mystic AIda: a row with its Public switch; the row
           opens the letter, which keeps every control it has. -->
      <div v-if="evaluation && memberMirror" class="mp-collapse" data-testid="mp-letter-row">
        <div class="mp-collapse-row">
          <button class="mp-collapse-head" @click="evalExpanded = !evalExpanded" :aria-expanded="evalExpanded">
            <span class="mp-collapse-title">A Letter from Mystic AIda</span>
            <i class="fas mp-collapse-caret" :class="evalExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
          </button>
          <div v-if="evalGenerated" class="mp-eval-share">
            <span class="mp-eval-vis-label">{{ evalStatus.is_public ? 'Public' : 'Private' }}</span>
            <label class="mp-switch" :title="evalStatus.is_public ? 'Shared on your profile' : 'Private'">
              <input type="checkbox" :checked="evalStatus.is_public" @change="toggleEvalPublic($event)" />
              <span class="mp-switch-track"><span class="mp-switch-knob"></span></span>
            </label>
          </div>
        </div>
        <div v-show="evalExpanded" class="mp-collapse-body mp-eval">
          <!-- Locked: not enough readings -->
          <div v-if="!evalStatus.unlocked" class="mp-eval-locked">
            <p>Mystic AIda is still getting to know you - <b>{{ evalStatus.readings }}/{{ evalStatus.threshold }}</b> readings before she writes your letter.</p>
            <div class="mp-eval-progress"><div class="mp-eval-progress-fill" :style="{ width: Math.min(100, (evalStatus.readings / evalStatus.threshold) * 100) + '%' }"></div></div>
          </div>

          <!-- Unlocked, not generated: reveal (25 carats) -->
          <div v-else-if="!evalGenerated" class="mp-eval-locked">
            <p>You've reached <b>{{ evalStatus.readings }}</b> readings - Mystic AIda is ready to write you a letter.</p>
            <button class="mp-eval-btn" :disabled="evalBusy" @click="revealEval">
              {{ evalBusy ? 'Mystic AIda is writing…' : `Receive my letter · ${evalStatus.cost} carats` }}
            </button>
            <p class="mp-eval-err" v-if="evalError">{{ evalError }}</p>
          </div>

          <!-- Generated: the letter -->
          <template v-else>
            <p class="mp-eval-text">{{ evaluation.display_text }}</p>
            <span class="mp-eval-date" v-if="evaluation.date">Written {{ evalDate }}</span>

            <!-- Length slider (same control as the journal) -->
            <div class="mp-eval-length">
              <div class="mp-eval-length-head"><span>Length - {{ evalWords }} words</span></div>
              <input type="range" min="80" max="500" step="10" v-model.number="evalWords"
                     class="mp-eval-range" :disabled="evalBusy" @change="setEvalLength(evalWords)" />
            </div>

            <!-- Massage: pops the wired Mystic AIda assistant (scope 'evaluation'). -->
            <div class="mp-eval-massage">
              <button class="mp-eval-btn" @click="openEvalAssistant">✦ Reshape this letter</button>
              <div class="mp-eval-actions">
                <button v-if="evaluation.versions && evaluation.versions.length" class="mp-eval-link" @click="showEvalVersions = !showEvalVersions">
                  ↻ Past Letters ({{ evaluation.versions.length }})
                </button>
                <button v-if="evalStatus.can_refresh" class="mp-eval-link gold" :disabled="evalBusy" @click="revealEval">
                  ✦ Refresh ({{ evalStatus.cost }} <CaratMark :size="13" />) - new readings since
                </button>
              </div>
              <div v-if="showEvalVersions" class="mp-eval-versions">
                <button v-for="(v, i) in evaluation.versions" :key="i" class="mp-eval-version" @click="restoreEval(i)">
                  <span class="mp-eval-version-n">v{{ i + 1 }}</span><span class="mp-eval-version-t">{{ v.slice(0, 90) }}…</span>
                </button>
              </div>
              <p class="mp-eval-err" v-if="evalError">{{ evalError }}</p>
            </div>
          </template>
        </div>
      </div>
      </div>

      <!-- Your own public profile, as anyone else sees it. -->
      <button v-if="memberMirror && !loading && profile.nick_name" type="button" class="mp-see-as" data-testid="mp-see-as-others" @click="seeAsOthers">See your Mirror as others do ›</button>
    </div>

    <!-- SHARED tab: everything put out, a single-row filmstrip of the shared
         readings, each shown as the lesson others see, each with its switch
         (the same toggleShare; off, the link stops working). -->
    <div class="mp-section" v-show="mirrorTab === 'shared'" data-testid="mp-panel-shared">
      <div class="mp-section-label">Your shared readings</div>
      <div v-if="readings.length" class="mp-strip-wrap">
        <button v-show="canLeft" class="mp-strip-nav mp-strip-prev" aria-label="Previous readings"
                @click="scrollStrip(-1)"><i class="fas fa-chevron-left"></i></button>
        <div class="mp-filmstrip" ref="stripRef" @scroll="updateArrows">
          <div v-for="r in readings" :key="r.short_uuid || r.title" class="mp-reading-wrap" data-testid="mp-shared-tile">
            <!-- The picture opens (picture-caption rev 1): full size, its caption
                 over its foot; the title stays the link to the reading. -->
            <div v-if="r.image_url" class="mp-reading mp-reading-pic">
              <div class="mp-reading-frame"><PictureOpen :src="r.image_url" :caption="r.caption || ''" small /></div>
              <a class="mp-reading-title" :href="readingHref(r)">{{ sharedTitle(r) }}</a>
            </div>
            <a v-else class="mp-reading" :href="readingHref(r)">
              <div class="mp-reading-noimg">✦</div>
              <span class="mp-reading-title">{{ sharedTitle(r) }}</span>
            </a>
            <label class="mp-shared-switch" :title="r.is_shared === false ? 'Private: the link does not work' : 'Shared: anyone with the link can see it'">
              <span class="mp-shared-state">{{ r.is_shared === false ? 'Private' : 'Shared' }}</span>
              <span class="mp-switch" :class="{ disabled: unsharing === r.short_uuid }">
                <input type="checkbox" :checked="r.is_shared !== false" :disabled="unsharing === r.short_uuid" @change="flipShare(r, $event)" />
                <span class="mp-switch-track"><span class="mp-switch-knob"></span></span>
              </span>
            </label>
          </div>
        </div>
        <button v-show="canRight" class="mp-strip-nav mp-strip-next" aria-label="More readings"
                @click="scrollStrip(1)"><i class="fas fa-chevron-right"></i></button>
      </div>
      <p v-else-if="!loading" class="mp-empty">You haven't shared any readings yet. Share one from a reading to see it here.</p>
    </div>

    <!-- CIRCLE tab (coven design 2026-10-26; was "My Seekers"): the coven's
         ring of 13 and the Outer Circle. Mounted when first opened. -->
    <div class="mp-section" v-show="mirrorTab === 'seekers'">
      <CircleTab v-if="circleSeen" :incoming="store.circle_incoming || ''" @incoming-done="store.circle_incoming = ''" />
    </div>

    <!-- LOOKS tab: the seasonal Mystic AIda looks this seeker has painted in
         the Community Voice "Conjure" flow (drafts + the one submitted), then
         the looks saved from the Voice gallery. -->
    <div class="mp-section" v-show="mirrorTab === 'looks'" data-testid="mp-panel-looks">
      <div class="mp-section-label">Your conjured looks for Mystic AIda</div>
      <div v-if="candidates.length" class="mp-gallery-grid">
        <div v-for="c in candidates" :key="c.id" class="mp-gtile" @click="signLightbox = c.image_url">
          <span class="mp-gtile-img">
            <img :src="c.image_url" :alt="c.label" />
            <button v-if="!c.locked" class="mp-gtile-del" title="Delete this look" @click.stop="removeCandidate(c)">
              <i class="fas fa-trash"></i>
            </button>
          </span>
          <span class="mp-gtile-label">{{ c.label }}</span>
          <span v-if="c.submitted" class="mp-gtile-badge">
            <i :class="c.locked ? 'fas fa-lock' : 'fas fa-check'"></i> In the circle · {{ c.votes }} <CaratMark :size="13" />
          </span>
          <span v-else class="mp-gtile-badge is-draft">Draft</span>
        </div>
      </div>
      <p v-else-if="!loading" class="mp-empty">You haven't conjured a look yet. Open Community → Voice → Conjure your own to paint one.</p>

      <!-- Saved looks — community avatar candidates you've hearted (from the Voting
           Gallery). Reuses the app-wide favorites store. -->
      <div class="mp-section-label mp-section-label-spaced">Saved looks</div>
      <div v-if="savedLooks.length" class="mp-gallery-grid">
        <div v-for="look in savedLooks" :key="look.url" class="mp-gtile" @click="signLightbox = look.url">
          <span class="mp-gtile-img">
            <img :src="look.url" :alt="look.title" />
            <button class="mp-gtile-del" title="Remove from saved" @click.stop="unsaveLook(look)">
              <i class="fas fa-heart-broken"></i>
            </button>
          </span>
          <span class="mp-gtile-label">{{ look.title || 'Saved look' }}</span>
        </div>
      </div>
      <p v-else-if="!loading" class="mp-empty">No saved looks yet. Heart a candidate in Community → Voice → View Gallery to collect it here.</p>
    </div>
  </div>
</template>

<script setup>
/**
 * MirrorProfile — the seeker's shareable profile: avatar, sun/moon/rising signs, a
 * toggle to share their altar, and their shared readings. (Friend messages live
 * on their own Messages tab.)
 */
import { ref, reactive, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import CaratMark from '@/components/ui/CaratMark.vue'
import { store } from '@/store'
// The Community open (the server's nav, set by ReaderPage): the member's Mirror.
const memberMirror = computed(() => store.community_open !== false)
// The Mirror's sections from nav (mirror_sections, quest journey).
const hasSection = (k) => mirrorHas(k, { sections: store.mirror_sections, communityOpen: memberMirror.value })
import { formatAmount } from '@/utils/carats'
import { mirrorHas, mirrorTabKey } from '@/utils/mirror-sections'
import { getMirrorProfile, generateMirrorAbout, shareMirrorAbout, setAltarPublic, setAffirmationPublic, getBirthMoment, saveBirthMoment,
  generateEvaluation, setEvaluationLength, restoreEvaluation, shareEvaluation, getMyCandidates,
  deleteCandidate, getImageGallery, toggleFavoriteImage, toggleShare } from '@/api-service'
import BirthDetailsModal from '@/components/widgets/BirthDetailsModal.vue'
import { WINDOWS } from '@/utils/birth-moment'
import CircleTab from '@/components/ui/CircleTab.vue'
import PictureOpen from '@/components/ui/share/PictureOpen.vue'
import { buildSignList } from '@/utils/chart-signs'

const ZODIAC_GLYPHS = {
  Aries: '♈', Taurus: '♉', Gemini: '♊', Cancer: '♋', Leo: '♌', Virgo: '♍',
  Libra: '♎', Scorpio: '♏', Sagittarius: '♐', Capricorn: '♑', Aquarius: '♒', Pisces: '♓',
}
function glyph(s) { return ZODIAC_GLYPHS[s] || '✦' }

// True until the first load settles, either way. The panel used to have no
// idea it was waiting, so it rendered its EMPTY states — "You haven't shared
// any readings yet" — as confident claims about the seeker while the answer
// was still in flight. A blank card is confusing; a wrong sentence is worse.
const loading = ref(true)
const profile = reactive({ nick_name: '', avatar_url: '', birth_location_name: '' })
const chart = reactive({ sun: null, moon: null, rising: null })
// About blurb. `is_stale` is the server saying a blurb is owed — either none
// exists, or the chart moved under the one that does (a birth time arriving
// after the fact would otherwise leave a Sun-only paragraph under three cards).
const about = reactive({ text: '', has_blurb: false, is_stale: false, is_public: false })
const aboutWriting = ref(false)
const signs = reactive({ sun: null, moon: null, rising: null })   // custom sign images (or null)
const altar = reactive({ has_image: false, image_url: null, is_public: false })
const affirmation = reactive({ has_affirmation: false, text: '', is_public: false })
const readings = ref([])
const evaluation = ref(null)
const lightbox = ref(false)
const signLightbox = ref(null)

// Sun + Moon + Rising, defaulting to the glyph until a custom image is painted.
// The Moon and the Rising sign both need a birth time and place, so for most
// seekers this is just the Sun until they fill those in.
const signList = computed(() => buildSignList(chart, signs))

// The pencil opens the Mystic AIda sign-image builder (hosted in MysticAltar).
function editSign(which, value) {
  window.dispatchEvent(new CustomEvent('open-sign-builder', { detail: { which, sign: value } }))
}

// Birth-details modal (the Settings birthday form + map) — drives the signs.
const birthModalOpen = ref(false)
const savingBirth = ref(false)
const birthError = ref('')
const birthFieldErrors = ref(null)
// The saved moment (GET /birth/moment/), so the form and the Born line know
// whether the time is a clock, a part of the day, or "not sure".
const birthMoment = ref(null)
const MON_FULL = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December']
function fmt12h(t) {
  const m = (t || '').match(/^(\d{1,2}):(\d{2})/)
  if (!m) return ''
  let h = +m[1]
  const ap = h >= 12 ? 'pm' : 'am'
  h = h % 12 || 12
  return `${h}:${m[2]} ${ap}`
}
// The full "Born:" line under the name — date @ time + place.
const bornLabel = computed(() => {
  let bd = store.querent_birthdate || ''
  if (bd.includes('T')) bd = bd.split('T')[0]
  const m = bd.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return ''
  let s = `Born: ${MON_FULL[+m[2] - 1]} ${+m[3]}, ${m[1]}`
  const mt = birthMoment.value && birthMoment.value.time
  if (mt && mt.kind === 'window') {
    const w = WINDOWS.find((x) => x.key === mt.value)
    if (w) s += ` · ${w.label.toLowerCase()}`
  } else if (!mt || mt.kind === 'exact') {
    const t = fmt12h((store.querent_birthtime || '').slice(0, 5))
    if (t) s += ` @ ${t}`
  }
  const place = (profile.birth_location_name || '').trim()
  if (place) s += `  ${place}`
  return s
})
async function loadBirthMoment() {
  try { birthMoment.value = (await getBirthMoment()).moment } catch (e) { /* the form opens empty */ }
}
async function openBirth() {
  birthFieldErrors.value = null
  birthError.value = ''
  await loadBirthMoment()
  birthModalOpen.value = true
}
async function saveBirth({ moment }) {
  if (savingBirth.value) return
  savingBirth.value = true
  birthError.value = ''
  birthFieldErrors.value = null
  try {
    const r = await saveBirthMoment(moment)
    birthMoment.value = r.moment
    store.querent_birthdate = moment.birthdate
    store.querent_birthtime = r.moment && r.moment.time && r.moment.time.kind === 'exact' ? r.moment.time.value : ''
    if (moment.place) {
      store.querent_birth_location_lat = moment.place.latitude
      store.querent_birth_location_long = moment.place.longitude
    }
    birthModalOpen.value = false
    await load()   // refresh the chart (sun/moon/rising) from the new birth details
  } catch (e) {
    const errs = e && e.response && e.response.data && e.response.data.errors
    if (errs) birthFieldErrors.value = errs
    else birthError.value = 'Could not save your birth details. Try again.'
  } finally { savingBirth.value = false }
}

// Filmstrip paging (advance/back by a full viewport-worth of cards).
const stripRef = ref(null)
const canLeft = ref(false)
const canRight = ref(false)
function updateArrows() {
  const el = stripRef.value
  if (!el) { canLeft.value = canRight.value = false; return }
  canLeft.value = el.scrollLeft > 4
  canRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 4
}
function scrollStrip(dir) {
  const el = stripRef.value
  if (!el) return
  el.scrollBy({ left: dir * el.clientWidth, behavior: 'smooth' })
}

const initial = computed(() => (profile.nick_name || '?').trim().charAt(0).toUpperCase())
const evalDate = computed(() => {
  if (!evaluation.value?.date) return ''
  return new Date(evaluation.value.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
})
const evalStatus = computed(() => evaluation.value?.status || { readings: 0, threshold: 10, unlocked: false, cost: 25, is_public: false })
const evalGenerated = computed(() => !!evaluation.value?.title)
const evalBusy = ref(false)
const evalError = ref('')
const showEvalVersions = ref(false)
const mirrorTab = ref('profile')
// The Circle tab loads its roster when first opened, and opens itself for
// someone waiting (the Messages add-back on a full coven).
const circleSeen = ref(false)
watch(mirrorTab, (t) => { if (t === 'seekers') circleSeen.value = true }, { immediate: true })
watch(() => store.circle_incoming, (v) => { if (v && memberMirror.value) mirrorTab.value = 'seekers' }, { immediate: true })
// One-shot: open-circle asked for the Circle tab; consumed so a later Mirror visit opens on Profile.
watch(() => store.circle_open, (v) => { if (!v) return; if (hasSection('circle')) mirrorTab.value = 'seekers'; store.circle_open = false }, { immediate: true })
// "Get started" or a Quest star asks for a section (the Circle, Looks), by its
// new name or its old one (about, readings, conjurings).
function onMirrorGoTab (e) { const t = e && e.detail && e.detail.tab; const k = mirrorTabKey(t); if (k && hasSection(k)) mirrorTab.value = k }
// The Letter is a row under Profile's "What others see", collapsed until opened.
const evalExpanded = ref(false)

// Conjured Mystic AIda looks — loaded lazily the first time the tab is opened.
const candidates = ref([])
const savedLooks = ref([])   // hearted community avatar looks (source 'community_avatar')
const candidatesLoaded = ref(false)
async function loadCandidates() {
  candidates.value = await getMyCandidates(store.session_id)
  try {
    const g = await getImageGallery(store.session_id)
    savedLooks.value = (g && g.favorites ? g.favorites : []).filter(f => f.source === 'community_avatar')
  } catch (e) { savedLooks.value = [] }
  candidatesLoaded.value = true
}
async function unsaveLook(look) {
  try {
    await toggleFavoriteImage(store.session_id, look.url, { source: 'community_avatar', title: look.title || '' })
    savedLooks.value = savedLooks.value.filter(l => l.url !== look.url)
  } catch (e) { /* keep it on failure */ }
}
watch(mirrorTab, (t) => { if (t === 'looks' && !candidatesLoaded.value) loadCandidates() })
async function removeCandidate(c) {
  if (!window.confirm(`Delete this conjured look${c.submitted ? ' (it\'s in the circle)' : ''}? This can't be undone.`)) return
  try {
    await deleteCandidate(c.id, store.session_id)
    candidates.value = candidates.value.filter(x => x.id !== c.id)
  } catch (e) { /* leave the tile in place on failure */ }
}

const evalWords = ref(200)
watch(() => evaluation.value?.words, (w) => { if (typeof w === 'number') evalWords.value = w }, { immediate: true })

async function setEvalLength(words) {
  if (evalBusy.value) return
  evalBusy.value = true; evalError.value = ''
  try {
    const r = await setEvaluationLength(store.session_id, words)
    if (r && r.ok) evaluation.value = { ...evaluation.value, display_text: r.display_text, versions: r.versions, words: r.words }
    else evalError.value = 'Could not adjust length right now.'
  } catch (e) { evalError.value = 'Could not adjust length right now.' }
  finally { evalBusy.value = false }
}

// Massaging happens in the real wired Mystic AIda assistant (scope 'evaluation').
function openEvalAssistant() {
  window.dispatchEvent(new CustomEvent('altar-open-assistant', { detail: { open: true, scope: 'evaluation', toggle: true } }))
}
// The assistant applies a slant → reflect the new display + versions here.
function onEvaluationUpdated(e) {
  if (!evaluation.value || !e.detail) return
  evaluation.value = { ...evaluation.value, display_text: e.detail.display_text, versions: e.detail.versions || evaluation.value.versions }
}

async function revealEval() {
  if (evalBusy.value) return
  evalBusy.value = true; evalError.value = ''
  try {
    const r = await generateEvaluation(store.session_id)
    if (!r || !r.ok) {
      evalError.value = r && r.reason === 'insufficient' ? `Not enough carats - ${formatAmount(evalStatus.value.cost)} needed.`
        : r && r.reason === 'locked' ? 'Keep going - you need 10 readings first.'
        : 'Could not generate right now.'
    } else {
      evaluation.value = r
      if (typeof r.balance === 'number') store.querent_carat_balance = r.balance
    }
  } catch (e) { evalError.value = 'Could not generate right now.' }
  finally { evalBusy.value = false }
}

async function restoreEval(index) {
  const r = await restoreEvaluation(store.session_id, index)
  if (r && r.ok) { evaluation.value = { ...evaluation.value, display_text: r.display_text, versions: r.versions }; showEvalVersions.value = false }
}

async function toggleEvalPublic(e) {
  const val = e.target.checked
  try {
    const r = await shareEvaluation(store.session_id, val)
    evaluation.value = { ...evaluation.value, status: { ...evaluation.value.status, is_public: !!r.is_public } }
  } catch (err) { e.target.checked = evalStatus.value.is_public }
}

// Open the avatar builder (same form/behavior as the Altar's image generator).
function editAvatar() {
  window.dispatchEvent(new CustomEvent('open-avatar-builder'))
}
// The avatar builder set a new live avatar — reflect it on the Mirror.
function onAltarImageUpdated(e) {
  if (e.detail && e.detail.subject === 'avatar' && e.detail.url) profile.avatar_url = e.detail.url
}

// Toggle the Mystic AIda assistant scoped to the name picker (same as the Studio):
// the pencil opens it, and tapping again closes it.
function openNamePicker() {
  window.dispatchEvent(new CustomEvent('altar-open-assistant', { detail: { toggle: true, scope: 'name' } }))
}
// The assistant claimed a new name — reflect it.
function onNameUpdated(e) {
  if (e.detail && e.detail.name) profile.nick_name = e.detail.name
}

// The owner opens their reading in the app, as its owner reader (deck, avatar,
// and the Share screen where it can be made private all follow the reader).
// The public /s/ link is for other people.
function readingHref(r) {
  if (!r.short_uuid) return '#'
  const reader = r.reader_subdomain ? `reader=${encodeURIComponent(r.reader_subdomain)}&` : ''
  return `/?${reader}reading=${encodeURIComponent(r.short_uuid)}`
}
// What others see on a shared tile: the lesson's title when the server sends it.
function sharedTitle(r) { return r.lesson_title || r.title || r.summary_title || '' }
// Each shared reading's switch: the same toggleShare as the reading's Share
// screen. Off, the link stops working and the tile stays, Private, so it can
// be shared again from here.
const unsharing = ref('')
async function flipShare(r, e) {
  if (!r.short_uuid || unsharing.value) { if (e) e.target.checked = r.is_shared !== false; return }
  unsharing.value = r.short_uuid
  try {
    const res = await toggleShare(r.short_uuid)
    const rs = res && res.data && res.data.reading_session
    if (rs && typeof rs.is_shared === 'boolean') {
      readings.value = readings.value.map(x => (x.short_uuid === r.short_uuid ? { ...x, is_shared: rs.is_shared } : x))
    } else if (e) e.target.checked = r.is_shared !== false
  } catch (err) { if (e) e.target.checked = r.is_shared !== false }
  finally { unsharing.value = '' }
}

// "See your Mirror as others do": the public profile, opened on yourself.
function seeAsOthers() {
  window.dispatchEvent(new CustomEvent('open-seeker-modal', { detail: { nickName: profile.nick_name } }))
}

async function load() {
  // No session, nothing to wait for: the skeleton must not shimmer forever.
  if (!store.session_id) { loading.value = false; return }
  try {
    const d = await getMirrorProfile(store.session_id)
    if (!d || !d.ok) return
    profile.nick_name = d.nick_name || ''
    profile.avatar_url = d.avatar_url || ''
    profile.birth_location_name = d.birth_location_name || ''
    chart.sun = d.chart ? d.chart.sun : null
    chart.moon = d.chart ? d.chart.moon : null
    chart.rising = d.chart ? d.chart.rising : null
    signs.sun = d.signs ? d.signs.sun : null
    signs.moon = d.signs ? d.signs.moon : null
    signs.rising = d.signs ? d.signs.rising : null
    Object.assign(about, d.about || { text: '', has_blurb: false, is_stale: false, is_public: false })
    if (about.is_stale) writeAbout()
    Object.assign(altar, d.altar || {})
    Object.assign(affirmation, d.affirmation || {})
    readings.value = d.shared_readings || []
    evaluation.value = d.evaluation || null
    nextTick(updateArrows)
  } catch (e) { /* the skeleton clears either way — see finally */ } finally {
    loading.value = false
  }
}

async function writeAbout() {
  if (aboutWriting.value || !store.session_id) return
  aboutWriting.value = true
  try {
    const d = await generateMirrorAbout(store.session_id)
    if (d && d.ok) Object.assign(about, d)
  } catch (e) { /* the blurb is a grace note — never block the Mirror on it */ }
  finally { aboutWriting.value = false }
}

async function toggleAbout(e) {
  const next = e.target.checked
  about.is_public = next
  try {
    const d = await shareMirrorAbout(store.session_id, next)
    if (d && d.ok) about.is_public = d.is_public
  } catch (err) { about.is_public = !next }
}

async function toggleAltar(e) {
  const val = e.target.checked
  try {
    const r = await setAltarPublic(store.session_id, val)
    altar.is_public = !!r.is_public
  } catch (err) { e.target.checked = altar.is_public }
}

async function toggleAffirmation(e) {
  const val = e.target.checked
  try {
    const r = await setAffirmationPublic(store.session_id, val)
    affirmation.is_public = !!r.is_public
  } catch (err) { e.target.checked = affirmation.is_public }
}

onMounted(() => {
  // Load when the session ARRIVES, not only if it happens to exist at mount.
  // On a cold reload this component mounts before start-session comes back, so
  // store.session_id is still ''. load() bails on that, and a bare load() here
  // never tried again — leaving the seeker on a permanent placeholder ("Your
  // Mirror", a "?" avatar, no sun or rising card) while the endpoint behind it
  // was answering fine. Navigating away and back appeared to fix it, which is
  // why it read as slowness rather than as a bug.
  //
  // immediate:true covers the already-warm case; watch only fires on a CHANGE,
  // so a re-set of the same id doesn't refetch. Registered inside onMounted to
  // stay clear of the top-level-watch trap that bites in ReaderPage.
  watch(() => store.session_id, (id) => { if (id) { load(); if (store.querent_id) loadBirthMoment() } }, { immediate: true })

  window.addEventListener('resize', updateArrows)
  window.addEventListener('seeker-name-updated', onNameUpdated)
  window.addEventListener('altar-image-updated', onAltarImageUpdated)
  window.addEventListener('open-birth-modal', openBirth)
  window.addEventListener('evaluation-updated', onEvaluationUpdated)
  window.addEventListener('mirror-go-tab', onMirrorGoTab)
})
onUnmounted(() => {
  window.removeEventListener('resize', updateArrows)
  window.removeEventListener('seeker-name-updated', onNameUpdated)
  window.removeEventListener('altar-image-updated', onAltarImageUpdated)
  window.removeEventListener('open-birth-modal', openBirth)
  window.removeEventListener('evaluation-updated', onEvaluationUpdated)
  window.removeEventListener('mirror-go-tab', onMirrorGoTab)
})
defineExpose({ load })
</script>

<style scoped>
/* width:100% (not just max-width) so every tab renders at the SAME width — without
   it the flex column shrink-wraps .mp to each tab's content (the About sign cards
   are narrower than the Letter's text), shifting the header/avatar/tabs too. */
.mp { color: #e9dcff; padding: 18px 16px 28px; width: 100%; max-width: 640px;
  margin: 0 auto; box-sizing: border-box; }
.mp-page-title { font-family: 'Cinzel', serif; font-size: 18px; letter-spacing: 0.06em; color: #f0d9a3;
  text-align: left; margin: 0 0 18px; }

.mp-head { display: flex; flex-direction: row; align-items: center; gap: 22px; text-align: left; }
/* No overflow:hidden here — the image is rounded itself, so the edit badge can
   sit on the circle's edge without being clipped. */
.mp-avatar { position: relative; width: 160px; height: 160px; flex-shrink: 0; border-radius: 50%;
  border: 2px solid rgba(240,217,163,0.45);
  background: linear-gradient(160deg, rgba(93,59,140,0.5), rgba(28,16,48,0.6)); display: flex; align-items: center;
  justify-content: center; box-shadow: 0 12px 34px -12px rgba(0,0,0,0.85); }
.mp-avatar.clickable { cursor: zoom-in; }
.mp-avatar img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }
.mp-avatar-initial { font-family: 'Cinzel', serif; font-size: 64px; color: #f5e8c0; }
/* Edit pencil overlaid on the avatar (bottom-right) — gold fill so it stands out
   against the dark portrait. */
.mp-avatar-edit { position: absolute; bottom: 12px; right: 12px; width: 38px; height: 38px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center; cursor: pointer;
  background: linear-gradient(180deg, #f6e7b8, #d9b96a); border: 2px solid rgba(28,16,48,0.85);
  color: #2a1844; font-size: 14px; box-shadow: 0 3px 12px -2px rgba(0,0,0,0.9); transition: filter 0.2s, transform 0.1s; }
.mp-avatar-edit:hover { filter: brightness(1.08); }
.mp-avatar-edit:active { transform: scale(0.94); }
.mp-name-wrap { flex: 1; min-width: 0; display: flex; flex-direction: column; align-items: flex-start; gap: 8px; }
.mp-name-row { display: inline-flex; align-items: center; gap: 8px; }
.mp-name { text-align: left; font-family: 'Cinzel', serif; font-size: 24px; color: #f5e8c0; margin: 0; }
.mp-name-edit { background: none; border: none; color: #9b86c9; cursor: pointer; font-size: 13px;
  opacity: 0.5; transition: opacity 0.2s, color 0.2s; flex-shrink: 0; }
.mp-head:hover .mp-name-edit, .mp-name-edit:hover { opacity: 1; color: #f0d9a3; }
/* "Born:" line beneath the name. */
.mp-born { display: inline-flex; align-items: center; gap: 9px; background: none; border: none;
  cursor: pointer; padding: 0; font-family: inherit; font-size: 13px; color: #b9a9d8; text-align: left; }
.mp-born:hover { color: #e9dcff; }
.mp-born-icon { color: #f0d9a3; font-size: 13px; }
.mp-born-edit { color: #9b86c9; font-size: 11px; opacity: 0; transition: opacity 0.15s, color 0.15s; }
.mp-born:hover .mp-born-edit { opacity: 1; color: #f0d9a3; }
.mp-lightbox { position: fixed; inset: 0; z-index: 1350; background: rgba(0,0,0,0.85);
  display: flex; align-items: center; justify-content: center; padding: 24px; }
.mp-lightbox-img { max-width: 86vw; max-height: 82vh; border-radius: 16px; border: 1px solid rgba(240,217,163,0.25);
  box-shadow: 0 0 50px rgba(102,51,153,0.4); }
.mp-signs { display: flex; flex-direction: column; align-items: flex-end; gap: 8px; flex-shrink: 0; margin-top: 0; }
.mp-sign { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: #efe4c6;
  background: linear-gradient(180deg, rgba(240,217,163,0.14), rgba(240,217,163,0.05));
  border: 1px solid rgba(240,217,163,0.3); border-radius: 999px; padding: 4px 12px 4px 9px; }
.mp-sign em { color: #b9a9d8; font-style: normal; font-size: 10.5px; letter-spacing: 0.08em; text-transform: uppercase; }
.mp-glyph { font-size: 15px; color: #f6e7b8; line-height: 1; }
.mp-signs-click { cursor: pointer; }
.mp-signs-click:hover .mp-sign { border-color: rgba(240,217,163,0.55); }
.mp-add-signs { background: rgba(102,51,153,0.22); border: 1px solid rgba(155,134,201,0.45); color: #efe4c6;
  border-radius: 999px; padding: 6px 14px; font-size: 12.5px; cursor: pointer; font-family: inherit; }
.mp-add-signs:hover { background: rgba(102,51,153,0.4); color: #FFDFA3; }
.mp-add-signs-about { margin-bottom: 14px; }

/* Sun, Moon & Rising sign cards in the About tab — default glyph until a custom
   image. The Mirror is 640px wide (608px of content), so three cards side by side
   would leave each ~193px — under the ~200px a card needs before "Sagittarius"
   starts fighting the pencil for room. The 200px basis makes the row wrap to 2+1
   instead of crushing all three, and the name wraps rather than spilling its card
   at any width in between. */

/* One row, whatever the count. A 200px min-width meant three cards could not
   share a 640px column and the third wrapped onto a line of its own. Sizing
   from the count instead lets one sign fill the row, two split it, three share
   it — and the mobile rule below still stacks them. */
.mp-signs-about { display: flex; gap: 10px; margin-bottom: 18px; }
.mp-sign-card { display: flex; align-items: center; gap: 9px; flex: 1 1 0; min-width: 0;
  background: linear-gradient(160deg, rgba(93,59,140,0.16), rgba(28,16,48,0.3));
  border: 1px solid rgba(240,217,163,0.18); border-radius: 14px; padding: 10px 12px; }
.mp-sign-visual { flex: none; width: 48px; height: 48px; border-radius: 12px; padding: 0; border: 1px solid rgba(240,217,163,0.25);
  display: flex; align-items: center; justify-content: center; overflow: hidden; background: rgba(20,8,40,0.5); }
.mp-sign-visual.is-img { cursor: zoom-in; }
.mp-sign-visual.is-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
.mp-sign-visual.is-chip .mp-glyph { font-size: 24px; }
.mp-sign-text { display: flex; flex-direction: column; gap: 1px; min-width: 0; flex: 1 1 auto; }
.mp-sign-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mp-sign-name { font-family: 'Cinzel', serif; font-size: 15px; color: #f5e8c0; overflow-wrap: anywhere; }
.mp-sign-role { font-size: 10px; color: #b9a9d8; letter-spacing: 0.1em; text-transform: uppercase; }
.mp-sign-edit { margin-left: auto; flex: none; background: none; border: none; color: #9b86c9; cursor: pointer;
  font-size: 13px; opacity: 0.6; transition: opacity 0.2s, color 0.2s; }
.mp-sign-card:hover .mp-sign-edit, .mp-sign-edit:hover { opacity: 1; color: #f0d9a3; }

/* Birth-details modal (reuses the Settings birthday form + map). */
.mp-birth-overlay { position: fixed; inset: 0; z-index: 1360; background: rgba(0,0,0,0.8);
  display: flex; align-items: flex-start; justify-content: center; padding: 24px 12px; overflow-y: auto; }
.mp-birth-modal { width: 100%; max-width: 520px; background: #14071f; border: 1px solid rgba(240,217,163,0.2);
  border-radius: 16px; padding: 18px 20px 22px; color: #e9dcff; }
.mp-birth-head { display: flex; align-items: center; justify-content: space-between; }
.mp-birth-title { font-family: 'Cinzel', serif; font-size: 18px; color: #f5e8c0; margin: 0; }
.mp-birth-close { background: none; border: none; color: #9b86c9; font-size: 22px; line-height: 1; cursor: pointer; }
.mp-birth-close:hover { color: #f0d9a3; }
.mp-birth-sub { font-size: 12.5px; color: #9b86c9; margin: 6px 0 14px; }
.mp-birth-map { margin-top: 14px; }
.mp-birth-label { display: block; font-size: 11px; color: #9b86c9; margin-bottom: 6px; }
/* Give the OpenLayers widget a definite height (its root is height:100%, which
   collapses without a sized ancestor). */
.mp-birth-map :deep(.birthplace-map-widget) { height: 240px; border-radius: 10px; overflow: hidden; }
.mp-birth-map :deep(.map-wrapper) { border-radius: 10px; overflow: hidden; }
.mp-birth-error { font-size: 12px; color: #f0a3a3; margin: 10px 0 0; }
.mp-birth-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 16px; }
.mp-birth-ghost { background: transparent; color: #9b86c9; border: 1px solid rgba(155,134,201,0.4);
  border-radius: 999px; padding: 9px 18px; font-size: 13px; cursor: pointer; font-family: inherit; }
.mp-birth-save { background: linear-gradient(180deg, #7b4bb0, #4e2776); color: #fff; border: none;
  border-radius: 999px; padding: 9px 22px; font-size: 13px; font-weight: 700; cursor: pointer; font-family: inherit; }
.mp-birth-save:disabled { opacity: 0.55; cursor: default; }

/* Collapsible "A Letter from Mystic AIda" inside About */
.mp-collapse { margin-top: 16px; border-top: 1px solid rgba(255,255,255,0.08); padding-top: 6px; }
.mp-collapse-head { width: 100%; display: flex; align-items: center; justify-content: space-between;
  background: none; border: none; cursor: pointer; padding: 10px 2px; font-family: 'Cinzel', serif;
  font-size: 14px; color: #FFDFA3; }
.mp-collapse-head:hover { color: #fff7e6; }
.mp-collapse-caret { font-size: 12px; color: rgba(195,169,255,0.7); }
.mp-collapse-body { padding: 4px 2px 8px; }
/* The Letter's row: it opens the letter; its Public switch sits on the right. */
.mp-collapse-row { display: flex; align-items: center; gap: 12px; }
.mp-collapse-row .mp-collapse-head { flex: 1; min-width: 0; }
/* What others see (Profile), and the way to see it as they do. */
.mp-others { margin-top: 4px; }
.mp-see-as { display: block; margin: 22px 0 0; padding: 6px 0; background: none; border: none; cursor: pointer;
  font-family: inherit; font-size: 13px; color: #f0d9a3; text-align: left; }
.mp-see-as:hover { text-decoration: underline; }

/* Conjurings gallery */
.mp-gallery-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); gap: 12px; }
.mp-section-label-spaced { margin-top: 24px; }
.mp-gtile { display: flex; flex-direction: column; gap: 6px; cursor: zoom-in; }
.mp-gtile-img { position: relative; width: 100%; aspect-ratio: 1; border-radius: 11px; overflow: hidden; background: #150a28;
  border: 1px solid rgba(155,134,201,0.3); display: block; transition: border-color 0.15s, transform 0.15s; }
.mp-gtile-del { position: absolute; top: 6px; right: 6px; width: 24px; height: 24px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center; cursor: pointer; z-index: 2;
  background: rgba(12,6,26,0.7); border: 1px solid rgba(240,163,163,0.45); color: #f0a3a3; font-size: 10px;
  opacity: 0; transition: opacity 0.15s, background 0.15s; }
.mp-gtile:hover .mp-gtile-del { opacity: 1; }
.mp-gtile-del:hover { background: rgba(150,40,40,0.85); color: #fff; }
.mp-gtile:hover .mp-gtile-img { border-color: rgba(240,217,163,0.55); transform: translateY(-2px); }
.mp-gtile-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
.mp-gtile-label { font-family: 'Cinzel', serif; font-size: 12px; color: #f5e8c0; text-align: center; line-height: 1.2; }
.mp-gtile-badge { font-size: 10px; text-align: center; color: #f0d9a3;
  background: rgba(201,169,78,0.16); border: 1px solid rgba(201,169,78,0.35); border-radius: 999px; padding: 2px 8px; }
.mp-gtile-badge.is-draft { color: rgba(195,169,255,0.75); background: rgba(102,51,153,0.22); border-color: rgba(155,134,201,0.35); }
.mp-tagline { font-size: 12px; color: #9b86c9; font-style: italic; margin: 4px 0 0; }

.mp-section { margin-top: 16px; border-top: 1px solid rgba(255,255,255,0.08); padding-top: 14px; }
.mp-section-label { font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase; color: rgba(255,255,255,0.4); margin-bottom: 10px; }

.mp-altar-row { display: flex; align-items: center; gap: 14px; }
.mp-row-spaced { margin-top: 12px; }
.mp-affirm-thumb { font-family: 'Cinzel', serif; font-size: 30px; color: #8a78ad; }
.mp-altar-thumb { width: 64px; height: 64px; border-radius: 12px; overflow: hidden; flex-shrink: 0;
  border: 1px solid rgba(240,217,163,0.25); }
.mp-altar-thumb img { width: 100%; height: 100%; object-fit: cover; }
.mp-altar-empty { display: flex; align-items: center; justify-content: center; color: #6f5b95;
  background: rgba(20,8,40,0.5); font-size: 22px; }
.mp-altar-copy { flex: 1; min-width: 0; }
.mp-altar-title { font-size: 15px; color: #f5e8c0; font-weight: 600; }
.mp-altar-sub { font-size: 12px; color: #9b86c9; margin: 3px 0 0; }

/* Toggle switch */
.mp-switch { position: relative; cursor: pointer; flex-shrink: 0; }
.mp-switch.disabled { cursor: default; opacity: 0.5; }
.mp-switch input { position: absolute; opacity: 0; width: 0; height: 0; }
.mp-switch-track { display: block; width: 44px; height: 24px; border-radius: 999px; background: rgba(155,134,201,0.3);
  border: 1px solid rgba(155,134,201,0.45); transition: background 0.2s; }
.mp-switch-knob { position: absolute; top: 3px; left: 3px; width: 18px; height: 18px; border-radius: 50%;
  background: #e9dcff; transition: transform 0.2s; }
.mp-switch input:checked + .mp-switch-track { background: linear-gradient(180deg, #7b4bb0, #4e2776); border-color: rgba(240,217,163,0.5); }
.mp-switch input:checked + .mp-switch-track .mp-switch-knob { transform: translateX(20px); background: #f6e7b8; }

/* Single-row filmstrip — scrolls horizontally so the page never has to.
   Advance / back arrows page it by a full viewport-worth of cards. */
.mp-strip-wrap { position: relative; }
.mp-strip-nav { position: absolute; top: calc(50% - 12px); transform: translateY(-50%); z-index: 2;
  width: 34px; height: 34px; border-radius: 50%; border: 1px solid rgba(240,217,163,0.4);
  background: rgba(20,8,40,0.85); color: #f0d9a3; cursor: pointer; font-size: 13px;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 14px -4px rgba(0,0,0,0.8); transition: background 0.2s, color 0.2s; }
.mp-strip-nav:hover { background: rgba(102,51,153,0.85); color: #FFDFA3; }
.mp-strip-prev { left: -6px; }
.mp-strip-next { right: -6px; }
.mp-filmstrip { display: flex; gap: 12px; overflow-x: auto; overflow-y: hidden; padding-bottom: 8px;
  scrollbar-width: thin; scrollbar-color: rgba(155,134,201,0.5) transparent; -webkit-overflow-scrolling: touch; }
.mp-filmstrip::-webkit-scrollbar { height: 6px; }
.mp-filmstrip::-webkit-scrollbar-thumb { background: rgba(155,134,201,0.5); border-radius: 999px; }
.mp-reading { flex: 0 0 132px; width: 132px; display: flex; flex-direction: column; gap: 6px; text-decoration: none;
  color: inherit; border: 1px solid rgba(102,51,153,0.2); border-radius: 12px; overflow: hidden; background: rgba(20,8,40,0.4); }
.mp-reading:hover { border-color: rgba(240,217,163,0.4); }
.mp-reading-wrap { flex: 0 0 132px; width: 132px; display: flex; flex-direction: column; gap: 6px; }
.mp-reading-wrap .mp-reading { flex: none; width: 100%; }
/* Each shared tile's switch: "Shared" / "Private" to the left of it. */
.mp-shared-switch { display: flex; align-items: center; justify-content: space-between; gap: 8px; cursor: pointer; }
.mp-shared-state { font-size: 12px; color: #c3a9ff; }
.mp-reading img, .mp-reading-noimg { width: 100%; aspect-ratio: 1; object-fit: cover; display: block; }
.mp-reading-noimg { display: flex; align-items: center; justify-content: center; color: #6f5b95; font-size: 26px; }
.mp-reading-frame { width: 100%; aspect-ratio: 1; }
a.mp-reading-title { text-decoration: none; }
a.mp-reading-title:hover { color: #f0d9a3; }
.mp-reading-title { font-size: 12px; color: #d8c9f5; padding: 0 8px 8px; line-height: 1.35;
  overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }

/* "Your images" header + gallery link. */
.mp-section-head { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 8px; }
.mp-gallery-link { background: none; border: none; color: #f0d9a3; font-family: inherit; font-size: 13px;
  cursor: pointer; padding: 0; }
.mp-gallery-link:hover { text-decoration: underline; }
.mp-reading.mp-img { flex: 0 0 132px; padding: 0; cursor: pointer; font-family: inherit; text-align: left; }
.mp-empty { font-size: 13px; color: #9b86c9; font-style: italic; }

/* About — sits directly under the sign cards it is about, reading as a note
   rather than another card, so the three signs stay the visual anchor. */
.mp-about { margin: -4px 0 18px; padding: 14px 16px;
  background: linear-gradient(160deg, rgba(93,59,140,0.16), rgba(28,16,48,0.28));
  border: 1px solid rgba(240,217,163,0.12); border-left: 2px solid rgba(240,217,163,0.34);
  border-radius: 10px; }
.mp-about-text { margin: 0; font-size: 14px; line-height: 1.62; color: #e4d8ff; }
.mp-about-writing { display: flex; align-items: center; gap: 9px;
  color: #9b86c9; font-style: italic; }
.mp-about-dots { display: inline-flex; gap: 3px; }
.mp-about-dots i { width: 4px; height: 4px; border-radius: 50%;
  background: rgba(240,217,163,0.65); animation: mp-about-pulse 1.2s ease-in-out infinite; }
.mp-about-dots i:nth-child(2) { animation-delay: 0.18s; }
.mp-about-dots i:nth-child(3) { animation-delay: 0.36s; }
@keyframes mp-about-pulse { 0%, 100% { opacity: 0.25; } 50% { opacity: 1; } }
@media (prefers-reduced-motion: reduce) { .mp-about-dots i { animation: none; opacity: 0.7; } }
.mp-about-share { display: flex; align-items: center; justify-content: flex-end;
  gap: 10px; margin-top: 12px; padding-top: 11px;
  border-top: 1px solid rgba(240,217,163,0.10); }
.mp-about-share-label { font-size: 12px; color: #9b86c9; }

/* Loading skeletons. Deliberately the same shape and footprint as the content
   they stand in for, so nothing jumps when the data lands. */
.mp-sk { background: linear-gradient(90deg,
    rgba(195,169,255,0.07) 25%, rgba(195,169,255,0.15) 37%, rgba(195,169,255,0.07) 63%);
  background-size: 400% 100%; border-radius: 6px;
  animation: mp-sk-shimmer 1.4s ease-in-out infinite; }
.mp-sk-avatar { width: 160px; height: 160px; border-radius: 50%; flex: none; }
.mp-sk-visual { width: 48px; height: 48px; border-radius: 12px; flex: none; }
.mp-sk-line { border-radius: 4px; }
.mp-sk-card { border: 1px solid rgba(240,217,163,0.10); }
@keyframes mp-sk-shimmer { 0% { background-position: 100% 50%; } 100% { background-position: 0 50%; } }
/* A shimmer is decoration, not information — hold it still when asked. */
@media (prefers-reduced-motion: reduce) { .mp-sk { animation: none; } }

/* Mirror tabs (mirror the public Seeker profile) */
/* Never wrap. A wrapped tab lands BELOW the underline rule and reads as a
   broken row rather than a fourth tab; on a 379px screen "Conjurings" always
   fell off. Scroll the strip instead, which is the pattern the rest of the app
   uses for tab bars that outgrow their width. */
.mp-tabs { display: flex; gap: 6px; flex-wrap: nowrap; overflow-x: auto;
  -webkit-overflow-scrolling: touch; scrollbar-width: none;
  border-bottom: 1px solid rgba(93,59,140,0.25); margin: 6px 0 18px; }
.mp-tabs::-webkit-scrollbar { display: none; }
.mp-tab { flex: 0 0 auto; white-space: nowrap; }
.mp-tab { background: none; border: none; border-bottom: 2px solid transparent; color: rgba(195,169,255,0.5); font-family: 'Cinzel', serif; font-size: 13px; padding: 6px 2px 10px; margin-right: 16px; cursor: pointer; }
.mp-tab:hover { color: rgba(195,169,255,0.8); }
.mp-tab.active { color: #FFDFA3; border-bottom-color: #FFDFA3; }
.mp-eval-hint { display: block; font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic; font-size: 13px; color: rgba(195,169,255,0.55); margin: 8px 0 4px; }
/* Public/Private share toggle (matches the altar/affirmation toggles) */
.mp-eval-share { display: flex; align-items: center; gap: 8px; }
.mp-eval-vis-label { font-size: 10px; letter-spacing: 0.06em; text-transform: uppercase; color: rgba(195,169,255,0.6); }
/* Letter banner image */
.mp-eval-img-banner { width: 100%; height: 180px; border-radius: 12px; overflow: hidden; margin-bottom: 14px; border: 1px solid rgba(195,169,255,0.2); }
.mp-eval-img-banner img { width: 100%; height: 100%; object-fit: cover; display: block; }
/* Length slider — same control as the journal */
.mp-eval-length { margin: 18px 0 4px; }
.mp-eval-length-head { font-size: 12.5px; color: #d8c9f5; }
.mp-eval-range { -webkit-appearance: none; appearance: none; width: 100%; height: 4px; border-radius: 999px; background: rgba(155,134,201,0.35); outline: none; margin: 12px 0 4px; }
.mp-eval-range::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 16px; height: 16px; border-radius: 50%; background: #C3A9FF; cursor: pointer; box-shadow: 0 1px 4px rgba(0,0,0,0.4); }
.mp-eval-range::-moz-range-thumb { width: 16px; height: 16px; border: none; border-radius: 50%; background: #C3A9FF; cursor: pointer; }
.mp-eval-range:disabled { opacity: 0.5; }

/* Mystic AIda's evaluation */
.mp-eval-head { display: flex; align-items: center; justify-content: flex-end; }
.mp-eval-vis { font-size: 10px; letter-spacing: 0.06em; text-transform: uppercase; color: rgba(195,169,255,0.5); border: 1px solid rgba(195,169,255,0.25); border-radius: 999px; padding: 2px 9px; }
.mp-eval-vis.pub { color: #1a0f2e; background: linear-gradient(180deg,#FFDFA3,#cdae6a); border-color: transparent; }
.mp-eval-top { display: flex; gap: 16px; margin-bottom: 12px; }
.mp-eval-img { width: 110px; height: 110px; border-radius: 12px; overflow: hidden; flex: none; border: 1px solid rgba(195,169,255,0.25); }
.mp-eval-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
.mp-eval-title { font-family: 'Cinzel', serif; font-size: 18px; color: #FFDFA3; margin: 0 0 6px; }
.mp-eval-teaser { font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic; font-size: 14px; color: rgba(232,216,255,0.7); line-height: 1.5; margin: 0; }
.mp-eval-text { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 15px; color: rgba(255,255,255,0.55); line-height: 1.7; margin: 0 0 10px; white-space: pre-wrap; }
.mp-eval-date { font-size: 11px; color: rgba(195,169,255,0.45); }
.mp-eval-switch { display: inline-flex; align-items: center; gap: 8px; }
.mp-eval-vis-label { font-size: 10px; letter-spacing: 0.06em; text-transform: uppercase; color: rgba(195,169,255,0.6); }
/* Locked / reveal */
.mp-eval-locked { font-size: 14px; color: rgba(232,216,255,0.7); }
.mp-eval-locked b { color: #FFDFA3; }
.mp-eval-progress { height: 6px; border-radius: 999px; background: rgba(255,255,255,0.07); overflow: hidden; margin-top: 10px; }
.mp-eval-progress-fill { height: 100%; background: linear-gradient(90deg,#8a5fd6,#FFDFA3); border-radius: 999px; }
.mp-eval-btn { margin-top: 12px; font-family: 'Cinzel', serif; letter-spacing: 0.04em; font-size: 13px; color: #1a0f2e; background: linear-gradient(180deg,#FFDFA3,#cdae6a); border: none; border-radius: 10px; padding: 11px 22px; cursor: pointer; }
.mp-eval-btn:disabled { opacity: 0.6; cursor: default; }
.mp-eval-err { font-size: 12px; color: #e08fb0; margin: 10px 0 0; }
/* Massage */
.mp-eval-massage { margin-top: 18px; padding-top: 16px; border-top: 1px solid rgba(93,59,140,0.22); }
.mp-eval-massage-head { display: flex; flex-direction: column; gap: 2px; margin-bottom: 10px; }
.mp-eval-hint { font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic; font-size: 13px; color: rgba(195,169,255,0.55); }
.mp-eval-lenses { display: flex; flex-wrap: wrap; gap: 7px; margin-bottom: 10px; }
.mp-eval-lens { font-size: 12px; color: #d9c8f5; background: rgba(122,76,180,0.2); border: 1px solid rgba(150,110,220,0.35); border-radius: 999px; padding: 5px 12px; cursor: pointer; }
.mp-eval-lens:hover:not(:disabled) { background: rgba(122,76,180,0.4); }
.mp-eval-lens:disabled { opacity: 0.5; cursor: default; }
.mp-eval-freeform { display: flex; gap: 8px; margin-bottom: 10px; }
.mp-eval-input { flex: 1; background: rgba(255,255,255,0.04); border: 1px solid rgba(195,169,255,0.2); border-radius: 10px; color: #E8D8FF; font-size: 13px; padding: 8px 12px; outline: none; }
.mp-eval-input::placeholder { color: rgba(195,169,255,0.4); }
.mp-eval-send { font-family: 'Cinzel', serif; font-size: 12px; color: #1a0f2e; background: linear-gradient(180deg,#FFDFA3,#cdae6a); border: none; border-radius: 9px; padding: 0 16px; cursor: pointer; }
.mp-eval-send:disabled { opacity: 0.5; cursor: default; }
.mp-eval-actions { display: flex; flex-wrap: wrap; gap: 16px; }
.mp-eval-link { background: none; border: none; color: rgba(195,169,255,0.6); font-size: 12px; cursor: pointer; padding: 0; }
.mp-eval-link:hover:not(:disabled) { color: #C3A9FF; }
.mp-eval-link.gold { color: #FFDFA3; }
.mp-eval-link:disabled { opacity: 0.5; cursor: default; }
.mp-eval-versions { display: flex; flex-direction: column; gap: 6px; margin-top: 10px; }
.mp-eval-version { display: flex; gap: 8px; align-items: center; text-align: left; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); border-radius: 8px; padding: 7px 10px; cursor: pointer; color: rgba(232,216,255,0.7); font-size: 12px; }
.mp-eval-version:hover { border-color: rgba(255,223,163,0.4); }
.mp-eval-version-n { color: #FFDFA3; font-weight: 600; flex: none; }
.mp-eval-version-t { font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mp-eval-busy { font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic; font-size: 13px; color: rgba(195,169,255,0.6); margin: 8px 0 0; }

/* Phone layout — stack the header so a ~390px screen doesn't crush it. */
@media (max-width: 480px) {
  .mp-head { flex-direction: column; align-items: center; text-align: center; gap: 14px; }
  .mp-avatar { width: 112px; height: 112px; }
  .mp-name-wrap { align-items: center; }
  .mp-name { text-align: center; }
  /* One sign card per row on a phone: at ~358px of content, even two would
     leave each card too narrow for its glyph, name and pencil. */
  .mp-signs-about { flex-wrap: wrap; }
  .mp-sign-card { min-width: 0; flex: 1 1 100%; }
  .mp-strip-prev { left: 2px; }
  .mp-strip-next { right: 2px; }
}
</style>
